/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { getCustomerSupportUrl } from '@sportninja/common/utils/getClientUrls'
import { useContext, useState } from 'react'
import { connect } from 'react-redux'
import { Route, Switch, useHistory } from 'react-router-dom'
import { FrameContext } from '../../pages/App'
import ErrorScreen from '../ErrorScreen'
import Helmet from '../Helmet'
import { Desktop, media, Mobile } from '../Responsive'
import ContactInfoModal from './ContactInfoModal'
import PageMenu from './Menu'
import PageHeaderWithBackground from './PageHeader'
import MobilePageHeader from './MobileHeader'
import PageHeader from './Header'

const listFilter = (p) => typeof p !== 'undefined' && typeof p !== 'boolean'

const Page = ({
  loading,
  error,
  baseActions: unfilteredBaseActions = [],
  baseCrumb,
  baseRoute,
  baseTitle,
  breadcrumbs,
  // Opt in to per-crumb `to` paths; see PageHeader's prop docs.
  useCrumbPaths = false,
  pages: unfilteredPages = [],
  title: _title,
  isPrivate,
  refreshKey,
  iconName,
  imageId,
  isInFrame,
  titleActions: unfilteredTitleActions = [],
  // Top-aligns `titleActions` beside the title instead of baseline-aligning
  // them. See PageHeader's own prop docs — the event page's BETA pill opts in.
  titleActionsAlignTop = false,
  // Right-hand header slot (opposite the title). Passed straight through —
  // unlike titleActions/baseActions it is a single node, not a filtered list.
  headerActions,
  preserveQueryFor,
  backgroundImageUrl = null,
  subTitle = null,
  contactInfo = null,
  organizationId = null,
  organization = null,
  organizationPermission = null,
  onOnboardingChanged = null,
  isTeamOrSchedule,
  isSchedule,
  isOrganization = false,
  /** Event detail page — swaps the header glyph for a calendar mark. */
  isEvent = false,
  isSoccer,
  sport,
  genericSport,
  isOrgsPage = false,
  hideBreadcrumbs = false,
  displayMobileActions = true,
  competitionStartDate = null,
  competitionEndDate = null,
}) => {
  const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false)
  const frameConfig = useContext(FrameContext)

  const pages = unfilteredPages.filter(listFilter)
  const baseActions = unfilteredBaseActions.filter(listFilter)
  const titleActions = unfilteredTitleActions.filter(listFilter)
  const history = useHistory()

  const actions = isInFrame ? (
    []
  ) : (
    <>
      {baseActions}
      {!error && !loading && (
        <Switch>
          {pages?.length > 0 && pages[0].actions && (
            <Route exact path={baseRoute}>
              {pages[0].actions}
            </Route>
          )}
          {pages?.length > 1 &&
            pages.map((page) => {
              if (!page.actions) {
                return null
              }
              return (
                <Route key={page.to} exact path={page.to}>
                  {page.actions}
                </Route>
              )
            })}
        </Switch>
      )}
    </>
  )

  const privateEntityError =
    (error && error.status === 403) ||
    (error && error.status !== 404 && isPrivate)

  const title = _title

  let entityName = 'page'
  if (isOrganization) {
    entityName = 'organization'
  } else if (isSchedule) {
    entityName = 'Competition'
  } else if (isTeamOrSchedule) {
    entityName = 'team'
  }

  if (privateEntityError) {
    return (
      <>
        <Helmet title={'Private'} />
        <ErrorScreen
          onButtonPress={() =>
            window.open(
              getCustomerSupportUrl(),
              '_blank',
              'noopener,noreferrer'
            )
          }
          buttonText='Contact Support'
          title='Access Denied'
          message={t('common:xIsPrivate', { x: entityName })}
        />
      </>
    )
  } else if (error && error.status === 404) {
    return (
      <>
        <Helmet title={'Not Found'} />
        <ErrorScreen title={'Not Found'} message={error.message} />
      </>
    )
  }

  // `isEvent` has no tabs at all (it passes a single hidden page purely to
  // collapse the strip), so PageMenu's three loading placeholders would be
  // skeletons for tabs that never arrive. Scoped to the event flag rather than
  // widening the iframe condition, so no other page is affected.
  const shouldHidePageMenu =
    (isInFrame && frameConfig.hideHeader && frameConfig?.pages?.length === 1) ||
    isEvent

  return (
    <>
      {loading ? null : (
        <>
          {!isOrgsPage ? (
            <PageHeaderWithBackground
              backgroundImageUrl={backgroundImageUrl}
              title={title}
              subTitle={subTitle}
              iconName={iconName}
              imageId={imageId}
              organizationId={organizationId}
              organization={organization}
              organizationPermission={organizationPermission}
              onOnboardingChanged={onOnboardingChanged}
              isOrganization={isOrganization}
              sport={sport}
              genericSport={genericSport}
              isSoccer={isSoccer}
              isInFrame={isInFrame}
              titleActions={titleActions}
              titleActionsAlignTop={titleActionsAlignTop}
              headerActions={headerActions}
              baseActions={baseActions}
              pages={pages}
              isSchedule={isSchedule}
              isPrivate={isPrivate}
              error={error}
              loading={loading}
              refreshKey={refreshKey}
              baseCrumb={baseCrumb}
              baseRoute={baseRoute}
              breadcrumbs={breadcrumbs}
              useCrumbPaths={useCrumbPaths}
              history={history}
              setIsContactInfoModalOpen={setIsContactInfoModalOpen}
              isTeam={isTeamOrSchedule}
              isEvent={isEvent}
              contactInfo={contactInfo}
              competitionStartDate={competitionStartDate}
              competitionEndDate={competitionEndDate}
            />
          ) : null}
          <ContactInfoModal
            title={'Contact Info'}
            isFormOpen={isContactInfoModalOpen}
            setIsFormOpen={setIsContactInfoModalOpen}
            contactInfo={{
              email: contactInfo?.email,
              phone: contactInfo?.phone,
              website: contactInfo?.website,
              support_email: contactInfo?.support_email,
              support_phone: contactInfo?.support_phone,
              support_website: contactInfo?.support_website,
            }}
          />
        </>
      )}
      <Desktop>
        {(isTeamOrSchedule || isOrganization) && !isInFrame && (
          <div
            css={css`
              position: absolute;
              border-bottom: 1px solid ${colors.SECONDARY_200};
              background-color: #1b1f25;
              height: 48px;
              width: 150vw;
              left: -50%;
            `}
          />
        )}
      </Desktop>
      <div
        css={[
          css`
            margin: ${isTeamOrSchedule || isOrganization || isEvent ? 0 : 40}px
              auto;
            padding: 0px 88px;
            max-width: 1600px;
            position: relative;
            /*
             * 88px each side is 176px of the viewport, which a 27" monitor can
             * spare and a 13" laptop cannot — it was the single biggest reason
             * the wide data tables (Team Balance especially) ran out of room
             * and started scrolling sideways at 1280. Below the 14-column
             * breakpoint the gutter drops to the design system's own outside
             * margin (COL_PADDING) so the content gets those pixels back.
             * Larger screens are untouched.
             */
            ${media.fourteen} {
              padding: 0px 24px;
            }
            ${media.mobile} {
              margin: ${isEvent ? 0 : 12}px auto;
              padding: 0px;
            }
          `,
          (isTeamOrSchedule || isOrganization) && isInFrame
            ? css`
                min-height: 1600px;
              `
            : false,
        ]}
      >
        <Helmet title={title ? title : ''} />
        {!frameConfig.hideHeader && isOrgsPage && !loading && (
          <Mobile>
            {(isMobile) =>
              isMobile ? (
                <MobilePageHeader
                  isTeamOrSchedule={isTeamOrSchedule || isOrganization}
                  title={title}
                  iconName={iconName}
                  imageId={imageId}
                  actions={actions}
                  baseCrumb={baseCrumb || baseRoute}
                  baseTitle={baseTitle}
                  breadcrumbs={breadcrumbs}
                  useCrumbPaths={useCrumbPaths}
                  isInFrame={isInFrame}
                  titleActions={titleActions}
                  titleActionsAlignTop={titleActionsAlignTop}
                  displayMobileActions={displayMobileActions}
                >
                  {actions}
                </MobilePageHeader>
              ) : (
                <PageHeader
                  isTeamOrSchedule={isTeamOrSchedule || isOrganization}
                  baseCrumb={baseCrumb || baseRoute}
                  baseTitle={baseTitle}
                  title={title}
                  breadcrumbs={breadcrumbs}
                  useCrumbPaths={useCrumbPaths}
                  loading={loading}
                  iconName={iconName}
                  imageId={imageId}
                  isInFrame={isInFrame}
                  titleActions={titleActions}
                  titleActionsAlignTop={titleActionsAlignTop}
                  hideBreadcrumbs={hideBreadcrumbs}
                >
                  {isTeamOrSchedule ? false : actions}
                </PageHeader>
              )
            }
          </Mobile>
        )}
        {!error && !shouldHidePageMenu && (
          <PageMenu
            isTeamOrSchedule={isTeamOrSchedule || isOrganization}
            isSchedule={isSchedule}
            baseRoute={baseRoute}
            pages={pages}
            loading={loading}
            actions={isInFrame ? [] : actions}
            isInFrame={isInFrame}
            preserveQueryFor={preserveQueryFor}
          />
        )}
        {!error && !loading && (
          <Switch>
            {pages?.length > 0 && (
              <Route exact path={baseRoute} key={`${baseRoute}-${refreshKey}`}>
                {pages[0].component}
              </Route>
            )}
            {pages?.length > 1 &&
              pages.map((page) => (
                <Route
                  exact={!page.to.endsWith('/feed')}
                  path={page.to.endsWith('/feed') ? `${page.to}/:id?` : page.to}
                  key={`${page.to}-${refreshKey}`}
                >
                  {page.component}
                </Route>
              ))}
          </Switch>
        )}
      </div>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(Page)
